import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
    Box,
    CircularProgress,
    Paper,
    Tooltip,
    Typography,
} from "@mui/material";
import React, { useMemo } from "react";
import { SlateStatus } from "./SlateIndicator";
import { RokuStatus } from "./RokuIndicator";

export type CatalogAvailabilityStatus = "live" | "scheduled" | "expired";

export type AvailsWidgetSelection =
    | { type: "overall"; slateStatus: Exclude<SlateStatus, null> }
    | { type: "roku"; rokuStatus: Exclude<RokuStatus, null> }
    | { type: "catalog"; status: CatalogAvailabilityStatus }
    | { type: "syncRequired"; days: 1 | 7 | 14 }
    | { type: "expiring"; bucket: "recentlyExpired" | "expiring" }
    | null;

export interface AvailsPlusSummaryData {
    asOf: string;
    /** Overall Slate (backend also keeps `overall` as a deprecated alias). */
    slate?: {
        synced: number;
        require_sync: number;
        not_exist: number;
        unknown?: number;
    };
    overall: {
        synced: number;
        require_sync: number;
        not_exist: number;
        unknown?: number;
    };
    roku?: {
        synced: number;
        require_sync: number;
        not_exist: number;
        unknown?: number;
    };
    syncRequiredSlate?: {
        next1d: number;
        next7d: number;
        next14d: number;
    };
    syncRequiredRoku?: {
        next1d: number;
        next7d: number;
        next14d: number;
    };
    catalog?: {
        live: number;
        scheduled: number;
        expired: number;
    };
    syncRequired: {
        next1d: number;
        next7d: number;
        next14d: number;
    };
    expiring: {
        recentlyExpired: number;
        expiring: number;
        windowDays?: number;
    };
}

const OVERALL_HINT =
    "Number of Avails that are in sync, out of sync, or not in Slate";
const ROKU_HINT =
    "Number of Avails that are in sync, out of sync, or not in Roku";
const CATALOG_HINT =
    "Displays the total number of active, scheduled, and past run titles.";
const SYNC_REQUIRED_HINT =
    "Number of Avails requiring sync (Slate or Roku) before their due date in 1, 7, or 14 days";
const EXPIRING_HINT =
    "Avails that have recently expired or are expiring within 14 days of the latest end date";

const STATUS_META: Array<{
    key: "synced" | "require_sync" | "not_exist";
    label: string;
    color: string;
}> = [
    { key: "synced", label: "Synced", color: "#2e7d32" },
    { key: "require_sync", label: "Out of Sync", color: "#ed6c02" },
    { key: "not_exist", label: "Not in Slate", color: "#d32f2f" },
];

const ROKU_META: Array<{
    key: Exclude<RokuStatus, null>;
    label: string;
    color: string;
}> = [
    { key: "synced", label: "Synced", color: "#2e7d32" },
    { key: "require_sync", label: "Out of Sync", color: "#ed6c02" },
    { key: "not_exist", label: "Not in Roku", color: "#d32f2f" },
];

const CATALOG_META: Array<{
    key: CatalogAvailabilityStatus;
    label: string;
    color: string;
}> = [
    { key: "live", label: "Active", color: "#2e7d32" },
    { key: "scheduled", label: "Scheduled", color: "#ed6c02" },
    { key: "expired", label: "Past Run", color: "#d32f2f" },
];

function DonutChart({
    segments,
}: {
    segments: Array<{ value: number; color: string }>;
}) {
    const size = 72;
    const stroke = 12;
    const radius = (size - stroke) / 2;
    const circumference = 2 * Math.PI * radius;
    const total = segments.reduce((sum, segment) => sum + segment.value, 0);

    let offset = 0;
    const arcs =
        total > 0
            ? segments
                  .filter((segment) => segment.value > 0)
                  .map((segment) => {
                      const length = (segment.value / total) * circumference;
                      const arc = {
                          color: segment.color,
                          dasharray: `${length} ${circumference - length}`,
                          dashoffset: -offset,
                      };
                      offset += length;
                      return arc;
                  })
            : [
                  {
                      color: "#e0e0e0",
                      dasharray: `${circumference} 0`,
                      dashoffset: 0,
                  },
              ];

    return (
        <Box
            component="svg"
            width={size}
            height={size}
            viewBox={`0 0 ${size} ${size}`}
            sx={{ flexShrink: 0 }}
            aria-hidden
        >
            {arcs.map((arc, index) => (
                <circle
                    key={`${arc.color}-${index}`}
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke={arc.color}
                    strokeWidth={stroke}
                    strokeDasharray={arc.dasharray}
                    strokeDashoffset={arc.dashoffset}
                    strokeLinecap="butt"
                    transform={`rotate(-90 ${size / 2} ${size / 2})`}
                />
            ))}
        </Box>
    );
}

function WidgetShell({
    title,
    titleAddon,
    hint,
    children,
    minWidth = 200,
}: {
    title: string;
    titleAddon?: React.ReactNode;
    hint: string;
    children: React.ReactNode;
    minWidth?: number;
}) {
    return (
        <Paper
            variant="outlined"
            sx={{
                px: 1.75,
                py: 1.5,
                minWidth,
                flex: "0 1 auto",
                bgcolor: "background.paper",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    mb: 1,
                }}
            >
                <Typography variant="subtitle2" fontWeight={700} color="text.primary">
                    {title}
                </Typography>
                {titleAddon}
                <Tooltip title={hint} arrow>
                    <InfoOutlinedIcon
                        sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }}
                    />
                </Tooltip>
            </Box>
            {children}
        </Paper>
    );
}

function CountButton({
    label,
    value,
    color,
    selected,
    onClick,
}: {
    label: string;
    value: number;
    color?: string;
    selected?: boolean;
    onClick: () => void;
}) {
    return (
        <Box
            component="button"
            type="button"
            onClick={onClick}
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                width: "100%",
                border: 0,
                backgroundColor: selected ? "action.selected" : "transparent",
                borderRadius: 1,
                px: 0.5,
                py: 0.35,
                cursor: "pointer",
                textAlign: "left",
                "&:hover": { backgroundColor: "action.hover" },
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                {color ? (
                    <Box
                        sx={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            bgcolor: color,
                            flexShrink: 0,
                        }}
                    />
                ) : null}
                <Typography variant="body2" color="text.secondary" noWrap>
                    {label}
                </Typography>
            </Box>
            <Typography
                variant="body2"
                sx={{ color: "primary.main", fontWeight: 600, flexShrink: 0 }}
            >
                {value}
            </Typography>
        </Box>
    );
}

interface AvailsSummaryWidgetsProps {
    summary: AvailsPlusSummaryData | null;
    loading?: boolean;
    selection: AvailsWidgetSelection;
    onSelect: (selection: AvailsWidgetSelection) => void;
}

const widgetsRowSx = {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "stretch",
    gap: 1.5,
    mb: 2,
    justifyContent: "flex-start",
} as const;

export default function AvailsSummaryWidgets({
    summary,
    loading = false,
    selection,
    onSelect,
}: AvailsSummaryWidgetsProps) {
    const slateCounts = summary?.slate ?? summary?.overall;
    const overallSegments = useMemo(() => {
        if (!slateCounts) return STATUS_META.map((item) => ({ value: 0, color: item.color }));
        return STATUS_META.map((item) => ({
            value: slateCounts[item.key] || 0,
            color: item.color,
        }));
    }, [slateCounts]);

    const catalogSegments = useMemo(() => {
        if (!summary?.catalog) {
            return CATALOG_META.map((item) => ({ value: 0, color: item.color }));
        }
        return CATALOG_META.map((item) => ({
            value: summary.catalog?.[item.key] || 0,
            color: item.color,
        }));
    }, [summary]);

    const rokuSegments = useMemo(() => {
        if (!summary?.roku) {
            return ROKU_META.map((item) => ({ value: 0, color: item.color }));
        }
        return ROKU_META.map((item) => ({
            value: summary.roku?.[item.key] || 0,
            color: item.color,
        }));
    }, [summary]);

    const toggleOverall = (slateStatus: Exclude<SlateStatus, null>) => {
        if (
            selection?.type === "overall" &&
            selection.slateStatus === slateStatus
        ) {
            onSelect(null);
            return;
        }
        onSelect({ type: "overall", slateStatus });
    };

    const toggleRoku = (rokuStatus: Exclude<RokuStatus, null>) => {
        if (selection?.type === "roku" && selection.rokuStatus === rokuStatus) {
            onSelect(null);
            return;
        }
        onSelect({ type: "roku", rokuStatus });
    };

    const toggleCatalog = (status: CatalogAvailabilityStatus) => {
        if (selection?.type === "catalog" && selection.status === status) {
            onSelect(null);
            return;
        }
        onSelect({ type: "catalog", status });
    };

    const toggleSyncRequired = (days: 1 | 7 | 14) => {
        if (selection?.type === "syncRequired" && selection.days === days) {
            onSelect(null);
            return;
        }
        onSelect({ type: "syncRequired", days });
    };

    const toggleExpiring = (bucket: "recentlyExpired" | "expiring") => {
        if (selection?.type === "expiring" && selection.bucket === bucket) {
            onSelect(null);
            return;
        }
        onSelect({ type: "expiring", bucket });
    };

    if (loading && !summary) {
        return (
            <Box sx={widgetsRowSx}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, minHeight: 96, flex: 1 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">
                        Loading summary…
                    </Typography>
                </Box>
            </Box>
        );
    }

    if (!summary) return null;

    return (
        <Box sx={widgetsRowSx}>
            <WidgetShell title="Overall Slate" hint={OVERALL_HINT} minWidth={260}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <DonutChart segments={overallSegments} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        {STATUS_META.map((item) => (
                            <CountButton
                                key={item.key}
                                label={item.label}
                                value={slateCounts?.[item.key] || 0}
                                color={item.color}
                                selected={
                                    selection?.type === "overall" &&
                                    selection.slateStatus === item.key
                                }
                                onClick={() => toggleOverall(item.key)}
                            />
                        ))}
                    </Box>
                </Box>
            </WidgetShell>

            <WidgetShell title="Overall Roku" hint={ROKU_HINT} minWidth={260}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <DonutChart segments={rokuSegments} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        {ROKU_META.map((item) => (
                            <CountButton
                                key={item.key}
                                label={item.label}
                                value={summary.roku?.[item.key] || 0}
                                color={item.color}
                                selected={
                                    selection?.type === "roku" &&
                                    selection.rokuStatus === item.key
                                }
                                onClick={() => toggleRoku(item.key)}
                            />
                        ))}
                    </Box>
                </Box>
            </WidgetShell>

            <WidgetShell title="Overall Catalog" hint={CATALOG_HINT} minWidth={260}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <DonutChart segments={catalogSegments} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        {CATALOG_META.map((item) => (
                            <CountButton
                                key={item.key}
                                label={item.label}
                                value={summary.catalog?.[item.key] || 0}
                                color={item.color}
                                selected={
                                    selection?.type === "catalog" &&
                                    selection.status === item.key
                                }
                                onClick={() => toggleCatalog(item.key)}
                            />
                        ))}
                    </Box>
                </Box>
            </WidgetShell>

            <WidgetShell title="Sync Required" hint={SYNC_REQUIRED_HINT} minWidth={200}>
                <CountButton
                    label="Next 1d"
                    value={summary.syncRequired.next1d}
                    selected={
                        selection?.type === "syncRequired" && selection.days === 1
                    }
                    onClick={() => toggleSyncRequired(1)}
                />
                <CountButton
                    label="Next 7d"
                    value={summary.syncRequired.next7d}
                    selected={
                        selection?.type === "syncRequired" && selection.days === 7
                    }
                    onClick={() => toggleSyncRequired(7)}
                />
                <CountButton
                    label="Next 14d"
                    value={summary.syncRequired.next14d}
                    selected={
                        selection?.type === "syncRequired" && selection.days === 14
                    }
                    onClick={() => toggleSyncRequired(14)}
                />
            </WidgetShell>

            <WidgetShell
                title="Expiring"
                titleAddon={
                    <Typography variant="body2" color="text.secondary">
                        +/- 14d
                    </Typography>
                }
                hint={EXPIRING_HINT}
                minWidth={220}
            >
                <CountButton
                    label="Recently expired"
                    value={summary.expiring.recentlyExpired}
                    color="#d32f2f"
                    selected={
                        selection?.type === "expiring" &&
                        selection.bucket === "recentlyExpired"
                    }
                    onClick={() => toggleExpiring("recentlyExpired")}
                />
                <CountButton
                    label="Expiring"
                    value={summary.expiring.expiring}
                    color="#ed6c02"
                    selected={
                        selection?.type === "expiring" &&
                        selection.bucket === "expiring"
                    }
                    onClick={() => toggleExpiring("expiring")}
                />
            </WidgetShell>
        </Box>
    );
}
